// Older saved responses can retain this obsolete recipe-level diagnostic.
export const OBSOLETE_MATERIAL_OBSERVATION_WARNING = 'No approved material observation is active for this recipe.';

/** Curated display contract; all calculations remain server-owned. */
export interface ApproximateCostPlanSettings {
  enabled: boolean;
  /** Canonical persisted settings; each scope owns an exact confirmed quantity. */
  material_components: ApproximateCostPlanMaterialComponent[];
  /** Reusable source-release assemblies; deliberately separate from exact SKU components. */
  assembly_components: ConstructionAssemblyComponent[];
  /** Durable incomplete generated rows, excluded from priced canonical inputs. */
  pending_components?: PendingCostComponent[];
  dismissed_generated_component_ids?: string[];
  /** Source-neutral measured roof facts; no price or source can be entered here. */
  roof_work_packages: RoofWorkPackage[];
  /** Independently measured equipment/service occurrences, never inferred from roof area. */
  project_work_requirements: ProjectWorkRequirement[];
  /** Presentation order across component families; never used to select prices. */
  component_order: string[];
  /** Owner lifecycle references are re-grounded to the active server prefill. */
  component_takeoff: ComponentTakeoffConfirmation[];
  ai_planning_estimates_acknowledged: boolean;
  /** Read-only compatibility input accepted from old drafts; never emitted by normalization. */
  recipe_key?: string | null;
  work_item_description?: string | null; net_quantity?: string | null; quantity_unit?: string | null;
  /** Interior-paint recipe facts; never inferred from the browser or an LLM. */
  paint_coat_count?: string | null; paint_coverage_sf_per_gallon_per_coat?: string | null; paint_waste_fraction?: string | null;
  paint_coverage_basis?: 'planning_assumption' | null; paint_coverage_assumption_acknowledged?: boolean;
  setup_proposal?: { status: 'matched' | 'no_match' | 'ambiguous' | 'not_found'; candidate_description?: string; quantity?: string | null; unit?: string; source_stage?: number; source_label?: string; evidence_state?: string; recipe_key?: string | null } | null;
  board_waste_fraction: string;
  fee_assumption: { basis: 'markup_known_cost' | 'target_margin' | 'award_reference_share'; rate: string; rationale?: string } | null;
  other_costs: {
    name: string;
    amount: string | null;
    basis: string | null;
    required?: boolean;
    explicit_zero_reason?: string | null;
    /** Server-owned double-count treatment; legacy rows remain unclassified. */
    cost_treatment?: 'direct_project' | 'labor_support' | 'general_and_administrative' | 'site_supervision' | 'fee_profit' | 'unclassified';
  }[];
  /** Optional owner-configured planning policies; absent preserves server defaults. */
  indirect_allowance_settings?: IndirectAllowanceSettings;
  /** Owner-selected verified WD classifications.  The server owns wage matching. */
  labor_role_selections?: LaborRoleSelection[];
  site_staffing_roles?: SiteStaffingRoleSelection[];
  removed_site_staffing_role_kinds?: SiteStaffingRoleSelection['role_kind'][];
  labor_plan_provenance?: string | null;
}
export interface PendingCostComponent {
  occurrence_id: string;
  component_key?: ApproximateCostPlanMaterialComponent['component_key'] | null;
  assembly_key?: ConstructionAssemblyKey | null;
  component_kind: string;
  label: string;
  identity_provenance: 'source_stated' | 'planning_assumption';
  identity_assumptions: string[];
  quantity: string | null;
  unit: 'square_foot' | 'board_foot' | 'each' | null;
  quantity_provenance: 'directly_stated' | 'derived_from_cited_facts' | 'ai_planning_estimate' | 'missing' | 'ambiguous' | 'user_entered';
  configuration: Record<string, string | boolean | null>;
  source_proposal_id: string;
  source_occurrence_id: string;
  source_excerpt?: string;
  source_excerpt_sha256?: string;
  source_label?: string;
  source_locator?: string;
  missing_facts?: string[];
}

export interface SiteStaffingRoleSelection {
  role_key: string;
  role_kind: 'site_superintendent' | 'quality_control' | 'site_safety' | 'project_manager';
  soc_code: string;
  duty_description: string;
  mapping_reason: string;
  evidence_kind: 'verified_clause' | 'owner_cited_sow' | 'planning_default';
  evidence_reference: string;
  evidence_quote: string;
  separate_staffing_required: boolean;
  coverage_provider: 'contractor_personally' | 'separately_staffed';
  person_key: string;
  /** Explicit owner-reviewed wage proxy for one person who covers multiple site roles. */
  shared_primary_soc_code?: string | null;
  headcount?: string | null;
  active_workdays?: string | null;
  shift_hours?: string | null;
  average_concurrent_crew_size?: string | null;
  craft_scope_complete?: boolean;
}
export interface IndirectAllowanceSettings {
  employment_support_enabled: boolean;
  employment_support_percentage: string;
  general_and_administrative_enabled: boolean;
  general_and_administrative_percentage: string;
}
export interface LaborRoleSelection {
  role_key: string;
  classification: string;
  /** Exact verified WD row, used when identical classification names exist. */
  classification_row_id?: string;
  work_description: string;
  person_hours: string;
  hours_basis: string;
}
export interface ComponentTakeoffConfirmation {
  /** Persisted binding to the owner-confirmed component or assembly line. */
  component_occurrence_id?: string | null;
  proposal_id: string;
  occurrence_id: string;
  lifecycle: 'accepted' | 'edited_by_user' | 'rejected';
  quantity_provenance: 'directly_stated' | 'derived_from_cited_facts' | 'ai_planning_estimate' | 'user_entered';
}
export interface ApproximateCostPlanMaterialComponent {
  /** Stable project line; component_key remains the reusable catalog identity. */
  occurrence_id: string;
  component_key: 'drywall_level4_wood_v1' | 'interior_paint_flat_latex_v1' | 'softwood_lumber_2x4x16_no2' | '20a_125v_5_20r_self_test_gfci_receptacle' | '20a_125v_5_20r_standard_duplex_receptacle' | 'zero_voc_interior_eggshell_paint_5_gallon' | 'champion_pro_manual_water_closet_1_28_gpf' | 'ppg_speedhide_6_2_interior_primer';
  specification_id: string; quantity: string; unit: 'square_foot' | 'board_foot' | 'each'; confirmed: true;
  specification_basis?: 'planning_assumption' | null;
  board_waste_fraction?: string | null; paint_coat_count?: string | null;
  paint_coverage_sf_per_gallon_per_coat?: string | null; paint_waste_fraction?: string | null;
  paint_coverage_basis?: 'planning_assumption' | null; paint_coverage_assumption_acknowledged?: boolean;
  gfci_compatibility_confirmed?: boolean;
  duplex_compatibility_confirmed?: boolean;
  duplex_color?: 'brown' | 'ivory' | null;
  duplex_grade?: 'industrial' | null;
  paint_catalog_compatibility_confirmed?: boolean;
  water_closet_compatibility_confirmed?: boolean;
  /** PPG 6-2 facts are independently scoped from finish-paint assumptions. */
  primer_coat_count?: string | null;
  primer_coverage_sf_per_gallon_per_coat?: string | null;
  primer_waste_fraction?: string | null;
  primer_coverage_basis?: 'manufacturer_nominal' | 'planning_assumption' | null;
  primer_coverage_evidence_acknowledged?: boolean;
  primer_coverage_assumption_acknowledged?: boolean;
  primer_product_compatibility_confirmed?: boolean;
  primer_smooth_nonporous_confirmed?: boolean;
}
export type ConstructionAssemblyKey = 'wall_finish' | 'bagged_concrete_small_volume' | 'exterior_full_broadcast_coating' | 'tuff_hide_spray_surfacer' | 'three_tab_roof_shingles' | 'vct_flooring';
/** Owner-confirmed application facts only; source/product data stays server-side. */
export interface ConstructionAssemblyComponent {
  occurrence_id: string;
  assembly_key: ConstructionAssemblyKey;
  quantity: string;
  unit: 'square_foot';
  confirmed: true;
  specification_basis?: 'planning_assumption' | null;
  waste_fraction?: string;
  primer_coats?: string;
  finish_coats?: string;
  concrete_thickness_inches?: string;
  full_broadcast_suitability_confirmed?: boolean;
  airless_spray_confirmed?: boolean;
  replaces_conventional_skim_and_primer?: boolean;
  three_tab_compatibility_confirmed?: boolean;
  /** The limited VCT pilot supports only the released porous-concrete / S-515 system. */
  vct_substrate?: 'porous_concrete' | null;
  vct_application_method?: 's515_recommended_trowel' | null;
}
export interface RoofInsulationLayer {
  occurrence_id: string;
  area_sf?: string | null;
  thickness_inches?: string | null;
  r_value?: string | null;
  compressive_psi?: string | null;
  material?: 'xps' | 'eps' | 'polyiso' | 'other' | null;
}
export interface RoofWorkPackage {
  schema_version: 'roof-work-package-v1';
  occurrence_id: string;
  roof_area_sf?: string | null;
  demolition_area_sf?: string | null;
  panel_gauge?: number | null;
  panel_material?: 'galvanized_steel' | 'galvalume_steel' | 'aluminum' | 'other' | null;
  panel_profile?: 'standing_seam' | 'three_tab_shingle' | 'other' | null;
  fastening?: 'mechanical' | 'adhesive' | 'other' | null;
  insulation_layers: RoofInsulationLayer[];
}
export interface ProjectWorkRequirement {
  schema_version: 'project-work-requirement-v1';
  occurrence_id: string;
  work_key: string;
  scope_slot: string;
  quantity: string | null;
  unit: 'month' | 'day' | 'each' | 'load' | 'square_foot' | 'linear_foot' | 'cubic_yard' | 'gallon' | 'roll' | 'board' | 'carton';
  specification: Record<string, string>;
  activity?: 'material_supply' | 'equipment_rental' | 'temporary_facility' | 'disposal' | 'installation' | 'demolition' | 'installed_work' | 'modifier' | null;
  parent_occurrence_id?: string | null;
  roof_pipe_use_compatible?: boolean | null;
  roof_insulation_target_r?: string;
  roof_insulation_sow_minimum_r?: string | null;
  roof_insulation_sow_evidence_quote?: string | null;
  roof_insulation_waste_percent?: string;
  roof_insulation_configuration_id?: string;
  confirmed: true;
}
/** Exact catalog components whose verified local source release is active. */
export type ActiveCatalogMaterialComponentKey = '20a_125v_5_20r_self_test_gfci_receptacle' | '20a_125v_5_20r_standard_duplex_receptacle' | 'zero_voc_interior_eggshell_paint_5_gallon' | 'champion_pro_manual_water_closet_1_28_gpf' | 'ppg_speedhide_6_2_interior_primer';
export const normalizeActiveCatalogMaterialComponentKeys = (value: unknown): ActiveCatalogMaterialComponentKey[] => (
  Array.isArray(value) ? value.filter((item): item is ActiveCatalogMaterialComponentKey => (
    item === '20a_125v_5_20r_self_test_gfci_receptacle' || item === '20a_125v_5_20r_standard_duplex_receptacle' || item === 'zero_voc_interior_eggshell_paint_5_gallon' || item === 'champion_pro_manual_water_closet_1_28_gpf' || item === 'ppg_speedhide_6_2_interior_primer'
  )).filter((item, index, rows) => rows.indexOf(item) === index) : []
);
/** A cited SOW component that needs owner confirmation before it can enter Materials. */
export interface ComponentConfirmationCandidate {
  candidate_id: string; label: string; status: 'Specification confirmation needed';
  excerpt: string; source_label: 'SOW'; source_locator: string; quantity: string; unit: 'each'; missing_fields: string[];
  approved_option: {
    component_key: '20a_125v_5_20r_standard_duplex_receptacle'; label: string; specification: string;
    source_label: 'GSA Multiple Award Schedule catalog'; source_date: '2023-12-18'; source_url: string; disclosure: string;
  };
}
/** Cited project scope that remains unpriced until a later human review. */
export interface ComponentScopeReviewCandidate {
  label: string; excerpt: string; source_label: 'SOW'; source_locator: string;
  quantity_label: string; next_step: string; source_status: string;
}
/** Reject anything except the deliberately narrow, owner-facing review card. */
export const normalizeComponentConfirmationCandidates = (value: unknown): ComponentConfirmationCandidate[] => (
  Array.isArray(value) ? value.flatMap((item): ComponentConfirmationCandidate[] => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const raw = item as Record<string, unknown>;
    const option = raw.approved_option;
    if (!option || typeof option !== 'object' || Array.isArray(option)) return [];
    const approved = option as Record<string, unknown>;
    const candidate_id = text(raw.candidate_id).trim();
    const label = publicExplanation(raw.label);
    const excerpt = publicExplanation(raw.excerpt);
    const source_locator = /^page [1-9]\d{0,3}$/.test(text(raw.source_locator)) ? text(raw.source_locator) : '';
    const quantity = decimal(raw.quantity);
    const missing_fields = texts(raw.missing_fields).map(publicExplanation).filter(Boolean).slice(0, 3);
    const source_url = text(approved.source_url);
    const specification = publicExplanation(approved.specification);
    const disclosure = publicExplanation(approved.disclosure);
    if (
      !/^requirement:[a-z0-9_-]{3,120}$/i.test(candidate_id) || !label || !excerpt || !source_locator
      || !quantity || Number(quantity) <= 0 || raw.status !== 'Specification confirmation needed'
      || raw.source_label !== 'SOW' || raw.unit !== 'each' || !missing_fields.length
      || approved.component_key !== '20a_125v_5_20r_standard_duplex_receptacle'
      || approved.label !== 'Standard duplex receptacle'
      || approved.source_label !== 'GSA Multiple Award Schedule catalog' || approved.source_date !== '2023-12-18'
      || !/^https:\/\/www\.gsaadvantage\.gov\//.test(source_url) || !specification || !disclosure
    ) return [];
    return [{
      candidate_id, label, status: 'Specification confirmation needed', excerpt, source_label: 'SOW', source_locator,
      quantity, unit: 'each', missing_fields,
      approved_option: {
        component_key: '20a_125v_5_20r_standard_duplex_receptacle', label: 'Standard duplex receptacle',
        specification, source_label: 'GSA Multiple Award Schedule catalog', source_date: '2023-12-18', source_url, disclosure,
      },
    }];
  }).slice(0, 3) : []
);
/** Reject source paths, enum values, IDs, and any uncurated backend fields. */
export const normalizeComponentScopeReviewCandidates = (value: unknown): ComponentScopeReviewCandidate[] => (
  Array.isArray(value) ? value.flatMap((item): ComponentScopeReviewCandidate[] => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const raw = item as Record<string, unknown>;
    // A concise owner-facing name may legitimately be one word (for example,
    // "Roofing").  Still reject lowercase machine keys and identifiers.
    const rawLabel = text(raw.label).trim();
    const label = /^[A-Z][A-Za-z /&()-]{1,159}$/.test(rawLabel) ? rawLabel : publicExplanation(raw.label);
    const excerpt = publicExplanation(raw.excerpt);
    const source_locator = /^(?:page|line) [1-9]\d{0,4}$/.test(text(raw.source_locator)) ? text(raw.source_locator) : '';
    const quantity_label = publicExplanation(raw.quantity_label);
    const next_step = publicExplanation(raw.next_step);
    const source_status = publicExplanation(raw.source_status);
    if (!label || !excerpt || raw.source_label !== 'SOW' || !source_locator || !quantity_label || !next_step || !source_status) return [];
    return [{ label, excerpt, source_label: 'SOW', source_locator, quantity_label, next_step, source_status }];
  }).slice(0, 8) : []
);
export interface SupportedCostRecipe { recipe_key: string; label: string; scope: string; allowed_units: string[] }
/** Planning roles are not assertions that the contract requires separate staff. */
const defaultSiteStaffingRoles = (): SiteStaffingRoleSelection[] => (
  ['project_manager', 'site_superintendent', 'quality_control', 'site_safety'] as const
).map(role_kind => ({
  role_key: `site-default-${role_kind}`, role_kind, person_key: `person-default-${role_kind}`,
  soc_code: '', duty_description: '', mapping_reason: '', evidence_kind: 'planning_default',
  evidence_reference: '', evidence_quote: '', separate_staffing_required: false,
  coverage_provider: 'separately_staffed', headcount: '1', active_workdays: null,
  shift_hours: null, average_concurrent_crew_size: null, craft_scope_complete: false,
}));
export const emptyApproximateSettings = (): ApproximateCostPlanSettings => ({ enabled: false, material_components: [], assembly_components: [], pending_components: [], dismissed_generated_component_ids: [], roof_work_packages: [], project_work_requirements: [], component_order: [], component_takeoff: [], ai_planning_estimates_acknowledged: false, board_waste_fraction: '0.10', fee_assumption: null, other_costs: [], site_staffing_roles: defaultSiteStaffingRoles(), removed_site_staffing_role_kinds: [] });
export interface ApproximateSourceLine {
  label: string; amount: string | null; source_label: string; source_date: string;
  geography_label: string; assumptions: string[]; source_url?: string;
  /** Opaque server-owned display join key; omitted on legacy revisions. */
  line_id?: string;
}
export interface ApproximateSourceAdjustment {
  line_id: string;
  kind: 'direct_local' | 'wage_determination' | 'pax_area_time' | 'ppi_escalation' | 'identity';
  factor: string;
  input_amount: string;
  output_amount: string;
  source_date: string;
  estimate_date: string;
  source_geography: string;
  estimate_geography: string;
  source_label: string;
  source_url?: string;
  explanation: string;
  limitation?: string;
}
export interface ApproximateLaborLine extends ApproximateSourceLine {
  craft_label: string; quantity_label: string; hours_per_unit: string; hours: string;
  base_wage: string; fringe: string;
  /** Server-projected only from a verified schedule identity. */
  wage_determination_url?: string | null;
}
/**
 * A compact, server-curated explanation of whether the supported recipe has
 * enough eligible Wage Determination rates for a complete labor subtotal.
 * Values here are intentionally presentation text rather than worker status
 * codes or schedule internals.
 */
export interface ApproximateLaborCoverage {
  status: 'complete' | 'partial' | 'unavailable';
  reason?: string;
  missing_crafts: string[];
  provenance_label?: string;
}
/** A release-owned planning allowance. It is never a WD fringe, fee, or source quote. */
export interface ApproximateIndirectAllowance {
  kind: 'labor_employment_support' | 'general_and_administrative';
  label: string;
  percentage: string;
  base_amount: string;
  amount: string;
  basis: string;
  policy_version: string;
  provenance_label: string;
  /** Server-projected verified WD basic-wage contributors; absent in older revisions. */
  basis_lines?: Array<{ craft_label: string; hours: string; base_wage: string; basic_wages: string }>;
}
/**
 * Owner-facing role-plan state. Deliberately excludes the server role key and
 * WD row identity: they are durable server evidence, not browser controls.
 */
export interface ApproximateLaborRolePlan {
  display_name: string;
  source: 'recipe' | 'assembly' | 'user' | 'contract_clause';
  scope_reference: string | null;
  hours: string | null;
  hours_basis: string | null;
  required: boolean;
  estimation_state: 'priced' | 'unestimated';
  missing_reason: string | null;
  wd_mapping_state: 'explicit' | 'provisional' | 'missing';
  wage_determination_id: string | null;
}
/** Owner-facing source ledger nested with a durable ROM result. */
export interface ApproximateSourceChecked {
  label: string;
  detail?: string;
  outcome?: 'consulted' | 'reused' | 'no_matching_evidence' | 'unavailable' | 'not_available' | 'not_applicable';
  contribution?: 'used' | 'context' | 'not_used';
  evidence_count?: number;
  as_of?: string;
}
/**
 * A browser-safe statement about an approved, local construction-cost
 * snapshot that was actually read by this saved calculation.  The source
 * label and evidence kind are a closed presentation vocabulary: source IDs,
 * artifact paths, policy state, and unselected records never cross the API.
 */
export interface ApproximateLocalCostEvidence {
  source_label: 'Defense Logistics Agency procurement history' | 'GSA Global Supply facility-material catalog' | 'Georgia Department of Transportation bid tabs' | 'U.S. Geological Survey mineral and aggregate releases';
  evidence_kind: 'historical material observation' | 'catalog material price' | 'all-in installed civil work' | 'commodity context';
  contribution: 'used' | 'context' | 'checked_no_match';
  as_of?: string;
  detail?: string;
}
/**
 * A DLA observation may replace one reviewed recipe component, but it can
 * never be added to the previous component price. This intentionally exposes
 * no DLA record/contract identifiers, price values, or local snapshot paths.
 */
export interface ApproximateMaterialComponentReplacement {
  source_label: 'Defense Logistics Agency procurement history';
  component_label: string;
  release_date: string;
  package_label: string;
}
export interface ApproximateMaterialLine extends ApproximateSourceLine {
  specification: string; sku: string; consumption_label: string;
  purchased_quantity_label: string; package_label: string; unit_price: string;
}
/** One server-reconciled owner-facing component subtotal. It never exposes a component key or snapshot identity. */
export interface ApproximateMaterialComponentBreakdown {
  label: string;
  amount: string | null;
  source_labels: string[];
}
/**
 * A reviewed, all-in installed-work observation selected by the server for a
 * single work package.  This deliberately contains only owner-facing source
 * facts; snapshot, policy, match-review, and source-record metadata stay
 * server-side.
 */
export interface ApproximateInstalledWorkLine {
  work_package_id: string;
  label: string;
  amount: string;
  matched_quantity_label: string;
  evidence_label: string;
  source_label: string;
  source_date: string;
  geography_label: string;
  limitation: string;
  source_url: string;
}
/** A selected source-backed planning amount, not necessarily a complete installed price. */
export interface ApproximatePlanningWorkLine {
  occurrence_id: string;
  label: string;
  amount: string;
  price_kind: 'published_benchmark' | 'adjusted_proxy' | 'material_only_component' | 'installed_work';
  completeness: 'complete' | 'partial';
  category: 'equipment' | 'project_other' | 'materials' | 'installed_work';
  display_group?: 'direct_materials' | 'indirect_materials' | null;
  included_in_subtotal: boolean;
  quantity_label: string;
  unit_price_label: string;
  source_label: string;
  source_date: string;
  source_url: string;
  geography_label: string;
  basis: string;
  inclusions: string[];
  exclusions: string[];
  unknown_costs: string[];
  roof_insulation_details?: {
    target_r: string; achieved_r: string; total_thickness_inches: string; evidence_basis: string; configuration_id: string;
    specification_source_url: string;
    layers: Array<{ label: string; quantity: string; unit_price: string; amount: string; coverage_sf: string; r_value: string; thickness_inches: string; source_url: string; source_date: string }>;
  };
}
/** Server-projected scope coverage for the saved ROM; it never allocates a residual or infers a category in the browser. */
export interface ApproximateComponentCompleteness {
  basis: 'extracted_scope' | 'legacy_components';
  counts: { priced: number; unpriced: number; excluded: number };
  items: Array<{
    label: string;
    status: 'priced' | 'unpriced' | 'excluded';
    reason: string;
    provenance_label: string;
    source_label: string;
    source_locator: string;
  }>;
}
export interface ApproximateCostPlanPublic {
  scope_label: string; status_label: string; installed_quantity_label: string;
  purchased_quantity_label: string; assumptions: string[]; exclusions: string[];
  labor_total: string | null; materials_total: string | null; priced_subtotal: string | null;
  profit_margin?: { base_amount: string; low: string; high: string; low_rate: string; high_rate: string } | null;
  modeled_subtotal_range?: { low: string; high: string } | null;
  labor_lines: ApproximateLaborLine[]; material_lines: ApproximateMaterialLine[];
  site_role_total?: string;
  site_role_lines?: Array<{
    role_key: string; label: string; amount: string; hours: string; hourly_wage: string; hours_basis: string;
    soc_code: string; duty_description: string; mapping_reason: string;
    evidence_kind: 'verified_clause' | 'owner_cited_sow' | 'planning_default'; evidence_reference: string; evidence_quote: string;
    geography_label: string; geography_level: 'metro' | 'state' | 'national'; release_year: number;
    series_id: string; source_label: string; source_url?: string; source_retrieved_at: string;
    fallback_reason: string | null; limitation: string;
  }>;
  /** Cited site staffing with missing scenario facts; never included in totals. */
  unpriced_site_roles?: Array<{ role_key: string; label: string; reason: string }>;
  /** Applied, owner-reviewed schedule scenario attached to this saved calculation. */
  labor_plan_summary?: {
    provenance_label: string; source_label: string;
    coverage_state: 'covered_package_only'; review_state: 'applied' | 'needs_review';
    confidence: 'low' | 'medium' | 'high'; average_concurrent_crew: string;
    parallel_work: boolean; derived_site_days: string; shift_hours: string;
  };
  /** Omitted by legacy revisions; no browser default is synthesized. */
  indirect_allowances?: ApproximateIndirectAllowance[];
  /** Omitted by legacy revisions; never drives a browser-side labor amount. */
  labor_role_plan?: ApproximateLaborRolePlan[];
  /** Omitted by legacy revisions; only explains an already-priced source line. */
  source_adjustments?: ApproximateSourceAdjustment[];
  /** Present only when canonical component rows can reconcile to Materials exactly. */
  material_component_breakdown?: ApproximateMaterialComponentBreakdown[];
  labor_coverage?: ApproximateLaborCoverage | null;
  sources_checked?: ApproximateSourceChecked[];
  /** Omitted unless an approved active local snapshot was actually consulted. */
  cost_evidence?: ApproximateLocalCostEvidence[];
  /** DLA components that replaced, rather than supplemented, a recipe offer. */
  material_component_replacements?: ApproximateMaterialComponentReplacement[];
  /** Optional server-owned component coverage disclosure for this result. */
  component_completeness?: ApproximateComponentCompleteness;
  /** All-in installed work replaces recipe labor/material/other rows. */
  installed_work_lines?: ApproximateInstalledWorkLine[];
  /** Selected and safe public projection; source releases and anchors stay server-side. */
  planning_work_lines?: ApproximatePlanningWorkLine[];
  /** Saved, owner-added components without a selected amount; never summed. */
  unpriced_explicit_components?: Array<{ occurrence_id: string; label: string; category: 'materials' | 'project_other'; display_group?: 'direct_materials' | 'indirect_materials' | null; reason: string }>;
  other_costs: {
    label: string;
    amount: string | null;
    basis: string;
    /** Server-owned category for an automatic unpriced required gap. */
    estimate_category?: 'labor' | 'materials' | 'project_other';
    /** Omitted by old revisions; their unknown rows are safely treated as required. */
    required?: boolean;
  }[];
  /**
   * A browser-safe whole-award comparison. A conservative point is a distinct
   * server-selected reference and intentionally never has range endpoints.
   * Award ranges retain their source low/median/high values. The browser does
   * not accept any demo-only reference shape.
   */
  award_reference: {
    reference_kind?: 'legacy_award_range' | 'conservative_comparable_award_point';
    amount: string | null;
    low?: string | null;
    high?: string | null;
    basis_label: string;
    compatible: boolean;
    source_label?: 'Previous federal awards — USAspending';
    selection_rule?: 'lowest_qualified_not_below_modeled_subtotal' | 'highest_qualified_when_all_below_modeled_subtotal';
    geography_tier?: 'county_exact' | 'city_exact' | 'statewide';
    qualified_family_count?: number;
    source_date_label?: string;
    policy_version?: string;
  } | null;
  warnings?: string[];
}
const text = (v: unknown): string => typeof v === 'string' ? v.slice(0, 1000) : '';
const texts = (v: unknown): string[] => Array.isArray(v) ? v.filter(x => typeof x === 'string').slice(0, 40).map(text) : [];
// JSON produced by older saved revisions and a few backend serializers can
// represent a Decimal as a number. Normalize that safe browser value back to
// the canonical string form rather than blanking a required component field
// after reload.
const decimal = (v: unknown): string | null => {
  if (typeof v === 'string' && /^-?\d{1,15}(\.\d{1,10})?$/.test(v)) return v;
  if (typeof v === 'number' && Number.isFinite(v)) {
    const normalized = String(v);
    return /^-?\d{1,15}(\.\d{1,10})?$/.test(normalized) ? normalized : null;
  }
  return null;
};
const rows = (v: unknown): Record<string, any>[] => Array.isArray(v) ? v.filter(x => x && typeof x === 'object').slice(0, 100) : [];
const publicExplanation = (v: unknown): string => {
  const value = text(v).trim();
  // A result that is just an internal enum/code must never become the owner's
  // explanation. The server supplies a complete safe sentence when there is a
  // useful action or coverage limitation to describe.
  return value && /\s/.test(value) && !/^[a-z0-9_:-]+$/i.test(value) ? value : '';
};
const publicSourceDateLabel = (v: unknown): string => {
  const value = text(v).trim();
  return /^20\d{2}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])$/.test(value)
    ? value
    : publicExplanation(value);
};
const publicAwardReference = (value: unknown): ApproximateCostPlanPublic['award_reference'] => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const kind = raw.reference_kind;
  const amount = decimal(raw.amount);
  const basis_label = publicExplanation(raw.basis_label);
  if (kind === 'conservative_comparable_award_point') {
    const selection_rule = raw.selection_rule;
    const geography_tier = raw.geography_tier;
    const qualified_family_count = raw.qualified_family_count;
    const source_date_label = publicSourceDateLabel(raw.source_date_label);
    const policy_version = raw.policy_version === undefined ? undefined : text(raw.policy_version).trim();
    // The source label is deliberately closed: a point can only be a
    // USAspending reference, never an arbitrary server/browser price source.
    if (
      amount === null || raw.compatible !== true
      || raw.source_label !== 'Previous federal awards — USAspending' || !basis_label
      || (selection_rule !== 'lowest_qualified_not_below_modeled_subtotal' && selection_rule !== 'highest_qualified_when_all_below_modeled_subtotal')
      || (geography_tier !== 'county_exact' && geography_tier !== 'city_exact' && geography_tier !== 'statewide')
      || !Number.isInteger(qualified_family_count) || (qualified_family_count as number) < 3 || (qualified_family_count as number) > 10_000
      || !source_date_label || (policy_version !== undefined && !/^[a-z0-9][a-z0-9._-]{0,80}$/i.test(policy_version))
    ) return null;
    return {
      reference_kind: 'conservative_comparable_award_point', amount, compatible: true,
      source_label: 'Previous federal awards — USAspending', basis_label,
      selection_rule, geography_tier, qualified_family_count: qualified_family_count as number,
      source_date_label, ...(policy_version ? { policy_version } : {}),
    };
  }
  // Any new/unknown declared reference kind fails closed. Old revisions did
  // not carry a kind and retain the prior legacy-range display unchanged.
  if (kind !== undefined && kind !== 'legacy_award_range') return null;
  return {
    ...(kind === 'legacy_award_range' ? { reference_kind: 'legacy_award_range' as const } : {}),
    amount, low: decimal(raw.low), high: decimal(raw.high), basis_label: text(raw.basis_label), compatible: raw.compatible === true,
  };
};
const normalizedSiteStaffingRoles = (v: any): SiteStaffingRoleSelection[] => rows(v.site_staffing_roles).flatMap((raw): SiteStaffingRoleSelection[] => {
    const role_kind = raw.role_kind;
    const allowed: Record<string, string[]> = { site_superintendent: ['11-9021', '47-1011'], quality_control: ['47-4011', '11-9021'], site_safety: ['19-5011', '19-5012'], project_manager: ['13-1082', '11-9021'] };
    const role_key = text(raw.role_key);
    const person_key = text(raw.person_key);
    if (!allowed[role_kind] || (raw.soc_code !== '' && !allowed[role_kind].includes(raw.soc_code)) || !/^[a-z0-9][a-z0-9_-]{2,119}$/.test(role_key) || !/^[a-z0-9][a-z0-9_-]{2,119}$/.test(person_key)) return [];
    const evidence_kind = raw.evidence_kind === 'planning_default' ? 'planning_default' : raw.evidence_kind === 'verified_clause' ? 'verified_clause' : 'owner_cited_sow';
    const coverage_provider = raw.coverage_provider === 'contractor_personally' ? 'contractor_personally' : 'separately_staffed';
    const shared_primary_soc_code = text(raw.shared_primary_soc_code);
    return [{ role_key, role_kind, soc_code: raw.soc_code, duty_description: text(raw.duty_description).slice(0, 300), mapping_reason: text(raw.mapping_reason).slice(0, 300), evidence_kind, evidence_reference: text(raw.evidence_reference).slice(0, 180), evidence_quote: text(raw.evidence_quote).slice(0, 500), separate_staffing_required: raw.separate_staffing_required === true, coverage_provider, person_key, ...(shared_primary_soc_code ? { shared_primary_soc_code } : {}), headcount: decimal(raw.headcount), active_workdays: decimal(raw.active_workdays), shift_hours: decimal(raw.shift_hours), average_concurrent_crew_size: decimal(raw.average_concurrent_crew_size), craft_scope_complete: raw.craft_scope_complete === true }];
  }).slice(0, 12);
const siteStaffingRoles = (v: any): SiteStaffingRoleSelection[] => {
  const unique: SiteStaffingRoleSelection[] = [];
  for (const role of normalizedSiteStaffingRoles(v)) {
    const existing = unique.find((item) => item.role_kind === role.role_kind);
    if (!existing) {
      unique.push(role);
      continue;
    }
    // Saved drafts may contain both a manually edited role and a later SOW
    // extraction of that role. Keep the owner's first staffing scenario, but
    // recover the later citation and any missing pricing inputs.
    if (!existing.soc_code) existing.soc_code = role.soc_code;
    if (!existing.headcount) existing.headcount = role.headcount;
    if (!existing.shift_hours) existing.shift_hours = role.shift_hours;
    if (!existing.active_workdays && !existing.average_concurrent_crew_size) {
      existing.active_workdays = role.active_workdays;
      existing.average_concurrent_crew_size = role.active_workdays ? null : role.average_concurrent_crew_size;
      if (existing.average_concurrent_crew_size) existing.craft_scope_complete = role.craft_scope_complete;
    }
    if (!existing.duty_description) existing.duty_description = role.duty_description;
    if (!existing.mapping_reason) existing.mapping_reason = role.mapping_reason;
    const existingEvidenceComplete = existing.evidence_reference.trim().length >= 4 && existing.evidence_quote.trim().length >= 10;
    const laterEvidenceComplete = role.evidence_reference.trim().length >= 4 && role.evidence_quote.trim().length >= 10;
    if (!existingEvidenceComplete && laterEvidenceComplete) {
      existing.evidence_kind = role.evidence_kind;
      existing.evidence_reference = role.evidence_reference;
      existing.evidence_quote = role.evidence_quote;
    }
  }
  const removed = removedSiteStaffingRoleKinds(v);
  return [...unique, ...defaultSiteStaffingRoles().filter(role =>
    !unique.some(item => item.role_kind === role.role_kind) && !removed.includes(role.role_kind))];
};
const removedSiteStaffingRoleKinds = (v: any): SiteStaffingRoleSelection['role_kind'][] => {
  const kinds = defaultSiteStaffingRoles().map(role => role.role_kind);
  const raw: unknown[] = Array.isArray(v.removed_site_staffing_role_kinds)
    ? v.removed_site_staffing_role_kinds : [];
  return raw.filter((kind, index): kind is SiteStaffingRoleSelection['role_kind'] =>
    kinds.includes(kind as SiteStaffingRoleSelection['role_kind'])
      && raw.indexOf(kind) === index);
};
const nonnegativeDecimal = (value: unknown): string | null => {
  const normalized = decimal(value);
  return normalized !== null && Number(normalized) >= 0 ? normalized : null;
};
const indirectAllowances = (value: unknown): ApproximateIndirectAllowance[] => {
  if (!Array.isArray(value) || value.length > 2) return [];
  const allowances = value.flatMap((item): ApproximateIndirectAllowance[] => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const raw = item as Record<string, unknown>;
    const kind = raw.kind;
    const percentage = decimal(raw.percentage);
    const base_amount = nonnegativeDecimal(raw.base_amount);
    const amount = nonnegativeDecimal(raw.amount);
    const label = publicExplanation(raw.label);
    const basis = publicExplanation(raw.basis);
    const provenance_label = publicExplanation(raw.provenance_label);
    const policy_version = text(raw.policy_version).trim();
    if (
      (kind !== 'labor_employment_support' && kind !== 'general_and_administrative')
      || percentage === null || Number(percentage) < 0 || Number(percentage) > 1
      || base_amount === null || amount === null || !label || !basis || !provenance_label
      || !/^[a-z0-9][a-z0-9._-]{0,80}$/i.test(policy_version)
    ) return [];
    const basisLines = Array.isArray(raw.basis_lines) && raw.basis_lines.length > 0 && raw.basis_lines.length <= 80
      ? raw.basis_lines.flatMap((candidate): NonNullable<ApproximateIndirectAllowance['basis_lines']> => {
        if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return [];
        const line = candidate as Record<string, unknown>;
        const craft_label = text(line.craft_label).trim().slice(0, 300);
        const hours = nonnegativeDecimal(line.hours);
        const base_wage = nonnegativeDecimal(line.base_wage);
        const basic_wages = nonnegativeDecimal(line.basic_wages);
        return craft_label && hours !== null && base_wage !== null && basic_wages !== null
          ? [{ craft_label, hours, base_wage, basic_wages }] : [];
      }) : [];
    const validBasis = kind === 'labor_employment_support' && Array.isArray(raw.basis_lines)
      && basisLines.length === raw.basis_lines.length
      && basisLines.every((line) => Math.abs(Number(line.hours) * Number(line.base_wage) - Number(line.basic_wages)) < 0.011)
      && Math.abs(basisLines.reduce((total, line) => total + Number(line.basic_wages), 0) - Number(base_amount)) < 0.005;
    return [{ kind, label, percentage, base_amount, amount, basis, policy_version, provenance_label,
      ...(validBasis ? { basis_lines: basisLines } : {}),
    }];
  });
  return new Set(allowances.map((allowance) => allowance.kind)).size === allowances.length ? allowances : [];
};
const safeWageDeterminationId = (value: unknown): string | null => {
  const candidate = text(value).trim();
  return /^[A-Z]{2}\d{8,20}(?:\/\d{1,4})?$/i.test(candidate) ? candidate : null;
};
const laborRolePlan = (value: unknown): ApproximateLaborRolePlan[] => Array.isArray(value)
  ? value.flatMap((item): ApproximateLaborRolePlan[] => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const raw = item as Record<string, unknown>;
    const source = raw.source;
    const estimation_state = raw.estimation_state;
    const wd_mapping_state = raw.wd_mapping_state;
    const display_name = publicExplanation(raw.display_name);
    const scope_reference = raw.scope_reference == null ? null : publicExplanation(raw.scope_reference);
    const hours = raw.hours == null ? null : nonnegativeDecimal(raw.hours);
    const hours_basis = raw.hours_basis == null ? null : publicExplanation(raw.hours_basis);
    const missing_reason = raw.missing_reason == null ? null : publicExplanation(raw.missing_reason);
    const wage_determination_id = raw.wage_determination_id == null ? null : safeWageDeterminationId(raw.wage_determination_id);
    if (
      (source !== 'recipe' && source !== 'assembly' && source !== 'user' && source !== 'contract_clause')
      || (estimation_state !== 'priced' && estimation_state !== 'unestimated')
      || (wd_mapping_state !== 'explicit' && wd_mapping_state !== 'provisional' && wd_mapping_state !== 'missing')
      || !display_name || typeof raw.required !== 'boolean'
      || (raw.scope_reference != null && !scope_reference) || (raw.hours != null && hours === null)
      || (raw.hours_basis != null && !hours_basis) || (raw.missing_reason != null && !missing_reason)
      || (raw.wage_determination_id != null && !wage_determination_id)
      || (estimation_state === 'unestimated' && !missing_reason)
    ) return [];
    return [{ display_name, source, scope_reference, hours, hours_basis, required: raw.required,
      estimation_state, missing_reason, wd_mapping_state, wage_determination_id }];
  }).slice(0, 100)
  : [];
const laborCoverage = (v: unknown): ApproximateLaborCoverage | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const raw = v as Record<string, unknown>;
  if (raw.status !== 'complete' && raw.status !== 'partial' && raw.status !== 'unavailable') return null;
  const missing_crafts = texts(raw.missing_crafts).map(value => value.trim()).filter(Boolean).slice(0, 12);
  const reason = publicExplanation(raw.reason);
  const provenance_label = publicExplanation(raw.provenance_label);
  return {
    status: raw.status,
    missing_crafts,
    ...(reason ? { reason } : {}),
    ...(provenance_label ? { provenance_label } : {}),
  };
};
const componentCompleteness = (v: unknown): ApproximateComponentCompleteness | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const raw = v as Record<string, unknown>;
  if (raw.basis !== 'extracted_scope' && raw.basis !== 'legacy_components') return null;
  const countsRaw = raw.counts;
  if (!countsRaw || typeof countsRaw !== 'object' || Array.isArray(countsRaw)) return null;
  const counts = countsRaw as Record<string, unknown>;
  const validCount = (value: unknown): value is number => Number.isInteger(value) && (value as number) >= 0 && (value as number) <= 100;
  if (!validCount(counts.priced) || !validCount(counts.unpriced) || !validCount(counts.excluded)) return null;
  const items = Array.isArray(raw.items) ? raw.items.flatMap((item): ApproximateComponentCompleteness['items'] => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const entry = item as Record<string, unknown>;
    const status = entry.status;
    const label = publicExplanation(entry.label);
    const reason = publicExplanation(entry.reason);
    const provenance_label = publicExplanation(entry.provenance_label);
    const source_label = publicExplanation(entry.source_label);
    const source_locator = publicExplanation(entry.source_locator);
    if ((status !== 'priced' && status !== 'unpriced' && status !== 'excluded') || !label || !reason || !provenance_label || !source_label || !source_locator) return [];
    return [{ label, status, reason, provenance_label, source_label, source_locator }];
  }).slice(0, 100) : [];
  if (items.length !== counts.priced + counts.unpriced + counts.excluded) return null;
  if (items.filter((item) => item.status === 'priced').length !== counts.priced
    || items.filter((item) => item.status === 'unpriced').length !== counts.unpriced
    || items.filter((item) => item.status === 'excluded').length !== counts.excluded) return null;
  return { basis: raw.basis, counts: { priced: counts.priced, unpriced: counts.unpriced, excluded: counts.excluded }, items };
};
const sourcesChecked = (v: unknown): ApproximateSourceChecked[] => Array.isArray(v)
  ? v.flatMap((item): ApproximateSourceChecked[] => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const raw = item as Record<string, unknown>;
    const label = publicExplanation(raw.label);
    const detail = publicExplanation(raw.detail);
    const outcome = ['consulted', 'reused', 'no_matching_evidence', 'unavailable', 'not_available', 'not_applicable'].includes(String(raw.outcome))
      ? raw.outcome as ApproximateSourceChecked['outcome'] : undefined;
    const contribution = ['used', 'context', 'not_used'].includes(String(raw.contribution))
      ? raw.contribution as ApproximateSourceChecked['contribution'] : undefined;
    const evidenceCount = typeof raw.evidence_count === 'number' && Number.isInteger(raw.evidence_count)
      && raw.evidence_count >= 0 && raw.evidence_count <= 500 ? raw.evidence_count : undefined;
    const asOf = typeof raw.as_of === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw.as_of)
      && !Number.isNaN(Date.parse(raw.as_of)) ? raw.as_of : undefined;
    return label ? [{ label, ...(detail ? { detail } : {}), ...(outcome ? { outcome } : {}), ...(contribution ? { contribution } : {}),
      ...(evidenceCount !== undefined ? { evidence_count: evidenceCount } : {}), ...(asOf ? { as_of: asOf } : {}) }] : [];
  }).slice(0, 25)
  : [];
const LOCAL_COST_EVIDENCE_KINDS: Record<ApproximateLocalCostEvidence['source_label'], ApproximateLocalCostEvidence['evidence_kind']> = {
  'Defense Logistics Agency procurement history': 'historical material observation',
  'GSA Global Supply facility-material catalog': 'catalog material price',
  'Georgia Department of Transportation bid tabs': 'all-in installed civil work',
  'U.S. Geological Survey mineral and aggregate releases': 'commodity context',
};
const localCostEvidence = (v: unknown): ApproximateLocalCostEvidence[] => Array.isArray(v)
  ? v.flatMap((item): ApproximateLocalCostEvidence[] => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const raw = item as Record<string, unknown>;
    const source_label = publicExplanation(raw.source_label) as ApproximateLocalCostEvidence['source_label'];
    const evidence_kind = publicExplanation(raw.evidence_kind) as ApproximateLocalCostEvidence['evidence_kind'];
    const contribution = raw.contribution;
    if (
      !source_label || LOCAL_COST_EVIDENCE_KINDS[source_label] !== evidence_kind
      || !['used', 'context', 'checked_no_match'].includes(contribution as string)
    ) return [];
    const as_of = typeof raw.as_of === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw.as_of) ? raw.as_of : undefined;
    const detail = publicExplanation(raw.detail);
    return [{ source_label, evidence_kind, contribution: contribution as ApproximateLocalCostEvidence['contribution'], ...(as_of ? { as_of } : {}), ...(detail ? { detail } : {}) }];
  }).slice(0, 4)
  : [];
const materialComponentReplacements = (v: unknown): ApproximateMaterialComponentReplacement[] => Array.isArray(v)
  ? v.flatMap((item): ApproximateMaterialComponentReplacement[] => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const raw = item as Record<string, unknown>;
    const component_label = publicExplanation(raw.component_label);
    const package_label = publicExplanation(raw.package_label);
    const release_date = typeof raw.release_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw.release_date) ? raw.release_date : '';
    if (raw.source_label !== 'Defense Logistics Agency procurement history' || !component_label || !package_label || !release_date) return [];
    return [{ source_label: 'Defense Logistics Agency procurement history', component_label, release_date, package_label }];
  }).slice(0, 12)
  : [];
const MATERIAL_COMPONENT_LABELS = new Set(['Drywall', 'Interior painting', 'Exact 2x4x16 #2 lumber', '20A self-test GFCI receptacle', 'Standard duplex receptacle', 'PPG Speedhide 6-2 interior primer', 'Wall finish system', 'Small-volume bagged concrete', 'Exterior full-broadcast coating', 'Tuff-Hide spray surfacer', '3-tab roofing materials']);
const materialComponentLabel = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  if (MATERIAL_COMPONENT_LABELS.has(value)) return value;
  const match = /^(Drywall|Interior painting|Exact 2x4x16 #2 lumber|20A self-test GFCI receptacle|Standard duplex receptacle|PPG Speedhide 6-2 interior primer|Wall finish system|Small-volume bagged concrete|Exterior full-broadcast coating|Tuff-Hide spray surfacer|3-tab roofing materials) — line ([1-9]\d*)$/.exec(value);
  return match ? value : '';
};
const materialComponentBreakdown = (v: unknown, materialsTotal: string | null): ApproximateMaterialComponentBreakdown[] | undefined => {
  if (v === undefined) return undefined;
  if (!Array.isArray(v)) return [];
  const seen = new Set<string>();
  const result = v.flatMap((item): ApproximateMaterialComponentBreakdown[] => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const raw = item as Record<string, unknown>;
    const label = materialComponentLabel(raw.label);
    const amount = decimal(raw.amount);
    const source_labels = texts(raw.source_labels).map((value) => value.trim()).filter(Boolean).slice(0, 6);
    if (!label || seen.has(label)) return [];
    seen.add(label);
    return [{ label, amount, source_labels }];
  }).slice(0, 10);
  const total = result.reduce((sum, item) => sum + Number(item.amount ?? 0), 0);
  if (materialsTotal !== null && (!Number.isFinite(total) || total !== Number(materialsTotal))) return [];
  return result;
};
export function normalizeApproximateSettings(v: any): ApproximateCostPlanSettings | null {
  if (!v || typeof v !== 'object') return null;
  const site_staffing_roles = siteStaffingRoles(v);
  const fee = v.fee_assumption;
  const recipeKey = text(v.recipe_key) || null;
  const rawComponents = rows(v.material_components);
  const migratingLegacy = rawComponents.length === 0;
  const legacyComponent = recipeKey === 'drywall_level4_wood_v1' || recipeKey === 'interior_paint_flat_latex_v1'
    ? [{ component_key: recipeKey, specification_id: recipeKey === 'drywall_level4_wood_v1' ? 'single_layer_half_in_gypsum_board_level_4' : 'flat_latex_interior_paint_two_finish_coats', quantity: v.net_quantity ?? v.net_installed_area_sf, unit: v.quantity_unit ?? 'square_foot', confirmed: true, board_waste_fraction: v.board_waste_fraction, paint_coat_count: v.paint_coat_count ?? '2', paint_coverage_sf_per_gallon_per_coat: v.paint_coverage_sf_per_gallon_per_coat, paint_waste_fraction: v.paint_waste_fraction, paint_coverage_basis: v.paint_coverage_basis, paint_coverage_assumption_acknowledged: v.paint_coverage_assumption_acknowledged }]
    : [];
  const contract = {
      drywall_level4_wood_v1: {
        specification_id: 'single_layer_half_in_gypsum_board_level_4', unit: 'square_foot',
      },
      interior_paint_flat_latex_v1: {
        specification_id: 'flat_latex_interior_paint_two_finish_coats', unit: 'square_foot',
      },
      softwood_lumber_2x4x16_no2: {
        specification_id: 'softwood_dimension_lumber_2x4x16_no2', unit: 'board_foot',
      },
      '20a_125v_5_20r_self_test_gfci_receptacle': {
        specification_id: 'hubbell_gftwrst20iu_20a_125v_2p3w_nema5_20r_self_test_gfci', unit: 'each',
      },
      '20a_125v_5_20r_standard_duplex_receptacle': {
        specification_id: 'hubbell_hbl5352_standard_duplex_20a_125v_2p3w_nema5_20r', unit: 'each',
      },
      zero_voc_interior_eggshell_paint_5_gallon: {
        specification_id: 'ppg_6_5310_05_speedhide_zero_interior_eggshell_zero_voc_5_gallon', unit: 'square_foot',
      },
      champion_pro_manual_water_closet_1_28_gpf: {
        specification_id: 'american_standard_211aa_104_020_champion_pro_manual_water_closet_1_28_gpf_white_less_seat', unit: 'each',
      },
      ppg_speedhide_6_2_interior_primer: {
        specification_id: 'ppg_6_2_speedhide_interior_latex_quick_dry_sealer', unit: 'square_foot',
      },
    } as const;
  const material_components = (rawComponents.length ? rawComponents : legacyComponent).flatMap((raw, index): ApproximateCostPlanMaterialComponent[] => {
    const component_key = raw.component_key as ApproximateCostPlanMaterialComponent['component_key'];
    const quantity = decimal(raw.quantity);
    const unit = raw.unit;
    const specification_id = text(raw.specification_id);

    const componentPaintCoverage = decimal(raw.paint_coverage_sf_per_gallon_per_coat);
    const componentPaintWaste = decimal(raw.paint_waste_fraction);
    const componentPaintAcknowledged = componentPaintCoverage !== null && componentPaintWaste !== null
      && raw.paint_coverage_basis === 'planning_assumption' && raw.paint_coverage_assumption_acknowledged === true;
    if (
      (!quantity && !migratingLegacy)
      || raw.confirmed !== true
      || !Object.prototype.hasOwnProperty.call(contract, component_key)
      || specification_id !== contract[component_key].specification_id
      || unit !== contract[component_key].unit
    ) return [];
    const suppliedOccurrenceId = text(raw.occurrence_id);
    const occurrence_id = /^component:[a-z0-9][a-z0-9_-]{2,119}$/.test(suppliedOccurrenceId)
      ? suppliedOccurrenceId
      : `component:${component_key}-${index + 1}`;
    const base = {
      occurrence_id,
      component_key,
      specification_id,
      quantity: quantity ?? '',
      unit,
      confirmed: true as const,
      ...(raw.specification_basis === 'planning_assumption' ? { specification_basis: 'planning_assumption' as const } : {}),
    };
    // Component assumptions are intentionally a discriminated browser
    // payload.  Do not preserve paint fields merely because they existed on
    // an old single-recipe object: a component's key alone determines which
    // fields it is allowed to send back to the server.
    if (component_key === 'drywall_level4_wood_v1') {
      const board_waste_fraction = decimal(raw.board_waste_fraction);
      return [{ ...base, ...(raw.specification_basis === 'planning_assumption' ? { specification_basis: 'planning_assumption' as const } : {}), ...(board_waste_fraction !== null ? { board_waste_fraction } : {}) }];
    }
    if (component_key === 'interior_paint_flat_latex_v1' || component_key === 'zero_voc_interior_eggshell_paint_5_gallon') {
      const paint_coat_count = decimal(raw.paint_coat_count);
      return [{
        ...base,
        ...(paint_coat_count !== null ? { paint_coat_count } : {}),
        ...(componentPaintAcknowledged ? {
          paint_coverage_sf_per_gallon_per_coat: componentPaintCoverage,
          paint_waste_fraction: componentPaintWaste,
          paint_coverage_basis: 'planning_assumption' as const,
          paint_coverage_assumption_acknowledged: true,
        } : {}),
        ...(component_key === 'zero_voc_interior_eggshell_paint_5_gallon' && raw.paint_catalog_compatibility_confirmed === true ? { paint_catalog_compatibility_confirmed: true } : {}),
      }];
    }
    if (component_key === '20a_125v_5_20r_self_test_gfci_receptacle') {
      return [{ ...base, ...(raw.gfci_compatibility_confirmed === true ? { gfci_compatibility_confirmed: true } : {}) }];
    }
    if (component_key === '20a_125v_5_20r_standard_duplex_receptacle') {
      const duplex_color = raw.duplex_color === 'ivory' || raw.duplex_color === 'brown' ? raw.duplex_color : null;
      const duplex_grade = raw.duplex_grade === 'industrial' ? raw.duplex_grade : null;
      return [{
        ...base,
        ...(raw.duplex_compatibility_confirmed === true ? { duplex_compatibility_confirmed: true } : {}),
        ...(duplex_color ? { duplex_color } : {}),
        ...(duplex_grade ? { duplex_grade } : {}),
      }];
    }
    if (component_key === 'champion_pro_manual_water_closet_1_28_gpf') {
      return [{ ...base, ...(raw.water_closet_compatibility_confirmed === true ? { water_closet_compatibility_confirmed: true } : {}) }];
    }
    if (component_key === 'ppg_speedhide_6_2_interior_primer') {
      const primer_coat_count = decimal(raw.primer_coat_count);
      const primer_coverage_sf_per_gallon_per_coat = decimal(raw.primer_coverage_sf_per_gallon_per_coat);
      const primer_waste_fraction = decimal(raw.primer_waste_fraction);
      const primerEvidence = primer_coverage_sf_per_gallon_per_coat !== null
        && primer_waste_fraction !== null
        && ((raw.primer_coverage_basis === 'manufacturer_nominal' && raw.primer_coverage_evidence_acknowledged === true)
          || (raw.primer_coverage_basis === 'planning_assumption' && raw.primer_coverage_assumption_acknowledged === true));
      return [{
        ...base,
        ...(primer_coat_count !== null ? { primer_coat_count } : {}),
        ...(primerEvidence ? {
          primer_coverage_sf_per_gallon_per_coat,
          primer_waste_fraction,
          primer_coverage_basis: raw.primer_coverage_basis as 'manufacturer_nominal' | 'planning_assumption',
          ...(raw.primer_coverage_basis === 'manufacturer_nominal' ? { primer_coverage_evidence_acknowledged: true } : { primer_coverage_assumption_acknowledged: true }),
        } : {}),
        ...(raw.primer_product_compatibility_confirmed === true ? { primer_product_compatibility_confirmed: true } : {}),
        ...(raw.primer_smooth_nonporous_confirmed === true ? { primer_smooth_nonporous_confirmed: true } : {}),
      }];
    }
    return [base];
  });
  const assembly_components = rows(v.assembly_components).flatMap((raw, index): ConstructionAssemblyComponent[] => {
    const assembly_key = raw.assembly_key as ConstructionAssemblyKey;
    const quantity = decimal(raw.quantity);
    const waste_fraction = decimal(raw.waste_fraction) ?? '0';
    const occurrence_id = /^component:[a-z0-9][a-z0-9_-]{2,119}$/.test(text(raw.occurrence_id))
      ? text(raw.occurrence_id) : `component:${assembly_key}-${index + 1}`;
    if (!quantity || Number(quantity) <= 0 || Number(waste_fraction) < 0 || Number(waste_fraction) > 1
      || raw.confirmed !== true || raw.unit !== 'square_foot') return [];
    const base = { occurrence_id, assembly_key, quantity, unit: 'square_foot' as const, confirmed: true as const, ...(raw.specification_basis === 'planning_assumption' ? { specification_basis: 'planning_assumption' as const } : {}), ...(waste_fraction !== '0' ? { waste_fraction } : {}) };
    if (assembly_key === 'wall_finish') {
      const primer_coats = decimal(raw.primer_coats); const finish_coats = decimal(raw.finish_coats);
      return primer_coats && finish_coats && Number.isInteger(Number(primer_coats)) && Number.isInteger(Number(finish_coats)) && Number(primer_coats) > 0 && Number(finish_coats) > 0
        ? [{ ...base, primer_coats, finish_coats }] : [];
    }
    if (assembly_key === 'bagged_concrete_small_volume') {
      const concrete_thickness_inches = decimal(raw.concrete_thickness_inches);
      return concrete_thickness_inches && Number(concrete_thickness_inches) > 0 ? [{ ...base, concrete_thickness_inches }] : [];
    }
    if (assembly_key === 'exterior_full_broadcast_coating') return raw.full_broadcast_suitability_confirmed === true ? [{ ...base, full_broadcast_suitability_confirmed: true }] : [];
    if (assembly_key === 'tuff_hide_spray_surfacer') return raw.airless_spray_confirmed === true && raw.replaces_conventional_skim_and_primer === true
      ? [{ ...base, airless_spray_confirmed: true, replaces_conventional_skim_and_primer: true }] : [];
    if (assembly_key === 'three_tab_roof_shingles') return raw.three_tab_compatibility_confirmed === true ? [{ ...base, three_tab_compatibility_confirmed: true }] : [];
    if (assembly_key === 'vct_flooring') return raw.vct_substrate === 'porous_concrete' && raw.vct_application_method === 's515_recommended_trowel'
      ? [{ ...base, vct_substrate: 'porous_concrete', vct_application_method: 's515_recommended_trowel' }] : [];
    return [];
  });
  // Retired generic Roofing cards are intentionally not migrated into a
  // priced component. Only an owner-added exact Roof insulation occurrence
  // enters the current calculation.
  const roof_work_packages: RoofWorkPackage[] = [];
  const token = (value: unknown): string => /^[a-z0-9][a-z0-9_-]{2,119}$/.test(text(value)) ? text(value) : '';
  const project_work_requirements = rows(v.project_work_requirements).slice(0, 50).flatMap((raw): ProjectWorkRequirement[] => {
    const occurrence_id = /^work:[a-z0-9][a-z0-9_-]{2,119}$/.test(text(raw.occurrence_id)) ? text(raw.occurrence_id) : '';
    const work_key = token(raw.work_key);
    const scope_slot = token(raw.scope_slot);
    // Preserve an editable quantity (including zero) and its occurrence.
    // Readiness and the server require a positive completed value.
    const parsedQuantity = raw.quantity == null ? null : decimal(raw.quantity);
    const quantity = parsedQuantity !== null && Number(parsedQuantity) >= 0 ? parsedQuantity : null;
    const unit = raw.unit;
    if (raw.schema_version !== 'project-work-requirement-v1' || !occurrence_id || !work_key || !scope_slot
      || !['month', 'day', 'each', 'load', 'square_foot', 'linear_foot', 'cubic_yard', 'gallon', 'roll', 'board', 'carton'].includes(unit)
      || raw.confirmed !== true || !raw.specification || typeof raw.specification !== 'object' || Array.isArray(raw.specification)) return [];
    const specification: Record<string, string> = {};
    const entries = Object.entries(raw.specification);
    if (entries.length > 20) return [];
    for (const [key, value] of entries) {
      if (!/^[a-z][a-z0-9_]{0,79}$/.test(key) || typeof value !== 'string' || !value.trim() || value.length > 200) return [];
      specification[key] = value.trim();
    }
    const activity = ['material_supply', 'equipment_rental', 'temporary_facility', 'disposal', 'installation', 'demolition', 'installed_work', 'modifier'].includes(raw.activity)
      ? raw.activity as ProjectWorkRequirement['activity'] : null;
    const parent_occurrence_id = /^(work|roof):[a-z0-9][a-z0-9_-]{2,119}$/.test(text(raw.parent_occurrence_id))
      ? text(raw.parent_occurrence_id) : null;
    if (activity === 'modifier' && !parent_occurrence_id) return [];
    return [{ schema_version: 'project-work-requirement-v1', occurrence_id, work_key, scope_slot, quantity, unit, specification,
      activity, parent_occurrence_id,
      ...(work_key === 'roof_insulation_r30' ? {
        roof_insulation_target_r: typeof raw.roof_insulation_target_r === 'string' ? raw.roof_insulation_target_r : '30',
        roof_insulation_sow_minimum_r: raw.roof_insulation_sow_minimum_r == null ? null : text(raw.roof_insulation_sow_minimum_r),
        roof_insulation_sow_evidence_quote: text(raw.roof_insulation_sow_evidence_quote) || null,
        roof_insulation_waste_percent: typeof raw.roof_insulation_waste_percent === 'string' ? raw.roof_insulation_waste_percent : '0',
        roof_insulation_configuration_id: token(raw.roof_insulation_configuration_id) || 'lakefront_iso4x82_6_two_layer_r30',
      } : {}),
      ...(work_key === 'neoprene_roof_boot_3in' ? {
        roof_pipe_use_compatible: typeof raw.roof_pipe_use_compatible === 'boolean' ? raw.roof_pipe_use_compatible : null,
      } : {}), confirmed: true }];
  }).filter((item, index, all) => all.findIndex((other) => other.occurrence_id === item.occurrence_id) === index);
  const component_takeoff = rows(v.component_takeoff).flatMap((raw): ComponentTakeoffConfirmation[] => {
    const proposal_id = text(raw.proposal_id);
    const occurrence_id = text(raw.occurrence_id);
    const lifecycle = raw.lifecycle;
    const quantity_provenance = raw.quantity_provenance;
    if (!/^[a-z0-9][a-z0-9_-]{2,119}$/.test(proposal_id)
      || !/^[A-Za-z0-9][A-Za-z0-9_.:-]{2,159}$/.test(occurrence_id)
      || !['accepted', 'edited_by_user', 'rejected'].includes(lifecycle)
      || !['directly_stated', 'derived_from_cited_facts', 'ai_planning_estimate', 'user_entered'].includes(quantity_provenance)) return [];
    return [{
      proposal_id, occurrence_id,
      ...(/^component:[a-z0-9][a-z0-9_-]{2,119}$/.test(text(raw.component_occurrence_id)) ? { component_occurrence_id: text(raw.component_occurrence_id) } : {}),
      lifecycle: lifecycle as ComponentTakeoffConfirmation['lifecycle'],
      quantity_provenance: quantity_provenance as ComponentTakeoffConfirmation['quantity_provenance'],
    }];
  }).filter((item, index, values) => values.findIndex((candidate) => candidate.proposal_id === item.proposal_id && candidate.occurrence_id === item.occurrence_id) === index).slice(0, 25);
  const configurationFields = new Set(['paint_coat_count', 'paint_coverage_sf_per_gallon_per_coat', 'paint_waste_percent', 'paint_coverage_assumption_acknowledged', 'paint_catalog_compatibility_confirmed', 'gfci_compatibility_confirmed', 'duplex_compatibility_confirmed', 'duplex_color', 'duplex_grade', 'water_closet_compatibility_confirmed', 'primer_coat_count', 'primer_coverage_sf_per_gallon_per_coat', 'primer_waste_percent', 'primer_product_compatibility_confirmed', 'primer_smooth_nonporous_confirmed', 'primer_coverage_assumption_acknowledged', 'waste_percent', 'primer_coats', 'finish_coats', 'concrete_thickness_inches', 'full_broadcast_suitability_confirmed', 'airless_spray_confirmed', 'replaces_conventional_skim_and_primer', 'three_tab_compatibility_confirmed', 'vct_substrate', 'vct_application_method']);
  const assemblyTargets = ['wall_finish', 'bagged_concrete_small_volume', 'exterior_full_broadcast_coating', 'tuff_hide_spray_surfacer', 'three_tab_roof_shingles', 'vct_flooring'];
  const canonicalIds = new Set([...material_components, ...assembly_components].map(row => row.occurrence_id));
  const pending_components = rows(v.pending_components).flatMap((raw): PendingCostComponent[] => {
    const occurrence_id = text(raw.occurrence_id);
    const component_key = text(raw.component_key) as ApproximateCostPlanMaterialComponent['component_key'];
    const assembly_key = text(raw.assembly_key) as ConstructionAssemblyKey;
    if (!/^component:[a-zA-Z0-9_-]{1,100}$/.test(occurrence_id) || canonicalIds.has(occurrence_id)
      || !['source_stated', 'planning_assumption'].includes(raw.identity_provenance)
      || Boolean(component_key) === Boolean(assembly_key)
      || (component_key && !Object.prototype.hasOwnProperty.call(contract, component_key))
      || (assembly_key && !assemblyTargets.includes(assembly_key))) return [];
    const configuration = Object.fromEntries(Object.entries(raw.configuration && typeof raw.configuration === 'object' && !Array.isArray(raw.configuration) ? raw.configuration : {}).filter(([key, value]) => configurationFields.has(key) && (value === null || typeof value === 'boolean' || typeof value === 'string')));
    return [{ occurrence_id, ...(component_key ? { component_key } : { assembly_key }),
      component_kind: text(raw.component_kind), label: text(raw.label), missing_facts: texts(raw.missing_facts),
      identity_provenance: raw.identity_provenance, identity_assumptions: texts(raw.identity_assumptions),
      quantity: decimal(raw.quantity), unit: ['square_foot', 'board_foot', 'each'].includes(raw.unit) ? raw.unit : null,
      quantity_provenance: ['directly_stated', 'derived_from_cited_facts', 'ai_planning_estimate', 'missing', 'ambiguous', 'user_entered'].includes(raw.quantity_provenance) ? raw.quantity_provenance : 'missing',
      configuration: configuration as PendingCostComponent['configuration'], source_proposal_id: text(raw.source_proposal_id), source_occurrence_id: text(raw.source_occurrence_id),
      ...(text(raw.source_excerpt) ? { source_excerpt: text(raw.source_excerpt) } : {}), ...(text(raw.source_excerpt_sha256) ? { source_excerpt_sha256: text(raw.source_excerpt_sha256) } : {}),
      ...(text(raw.source_label) ? { source_label: text(raw.source_label) } : {}), ...(text(raw.source_locator) ? { source_locator: text(raw.source_locator) } : {}),
    }];
  }).filter((row, index, all) => all.findIndex(candidate => candidate.occurrence_id === row.occurrence_id) === index).slice(0, 50);
  const dismissed_generated_component_ids = Array.isArray(v.dismissed_generated_component_ids) ? Array.from(new Set(v.dismissed_generated_component_ids.filter((id: unknown): id is string => typeof id === 'string' && /^component:[a-zA-Z0-9_-]{1,100}$/.test(id)))).slice(0, 200) as string[] : [];
  const indirect_allowance_settings = (() => {
    const raw = v.indirect_allowance_settings;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
    const employment_support_percentage = decimal(raw.employment_support_percentage);
    const general_and_administrative_percentage = decimal(raw.general_and_administrative_percentage);
    if (typeof raw.employment_support_enabled !== 'boolean' || typeof raw.general_and_administrative_enabled !== 'boolean'
      || employment_support_percentage === null || general_and_administrative_percentage === null
      || Number(employment_support_percentage) < 0 || Number(employment_support_percentage) > 0.5
      || Number(general_and_administrative_percentage) < 0 || Number(general_and_administrative_percentage) > 0.5) return undefined;
    return { employment_support_enabled: raw.employment_support_enabled, employment_support_percentage, general_and_administrative_enabled: raw.general_and_administrative_enabled, general_and_administrative_percentage };
  })();
  const labor_role_selections = rows(v.labor_role_selections).flatMap((raw): LaborRoleSelection[] => {
    const role_key = text(raw.role_key);
    const classification = text(raw.classification).trim().slice(0, 160);
    const classification_row_id = text(raw.classification_row_id).trim();
    const work_description = text(raw.work_description);
    const person_hours = decimal(raw.person_hours);
    const hours_basis = text(raw.hours_basis);
    // A partially edited role remains local until Calculate is enabled.  The
    // server independently requires all fields and a positive hour count.
    if (!/^labor-role-[a-z0-9-]{3,120}$/.test(role_key)) return [];
    return [{ role_key, classification, ...( /^wdrow-[a-f0-9]{32}$/.test(classification_row_id) ? { classification_row_id } : {}), work_description, person_hours: person_hours ?? '', hours_basis }];
  }).filter((item, index, all) => all.findIndex((candidate) => candidate.role_key === item.role_key
    || (candidate.classification === item.classification && candidate.work_description === item.work_description)) === index).slice(0, 20);
  return {
    // A persisted selected supported recipe predates the removed confirmation
    // checkbox. Treat it as active when rehydrating old drafts as well.
    enabled: material_components.length > 0 || assembly_components.length > 0 || pending_components.length > 0 || project_work_requirements.length > 0 || site_staffing_roles.length > 0,
    material_components, assembly_components, pending_components, dismissed_generated_component_ids, roof_work_packages, project_work_requirements,
    component_order: (() => {
      const fallback = [...material_components, ...assembly_components, ...pending_components, ...project_work_requirements].map((item) => item.occurrence_id);
      const valid = new Set(fallback);
      const ordered = Array.isArray(v.component_order)
        ? v.component_order.slice(0, 90).filter((id: unknown): id is string => typeof id === 'string' && valid.has(id))
        : [];
      return Array.from(new Set([...ordered, ...fallback]));
    })(),
    component_takeoff,
    ai_planning_estimates_acknowledged: v.ai_planning_estimates_acknowledged === true,
    work_item_description: text(v.work_item_description) || null,
    setup_proposal: v.setup_proposal && ['matched', 'no_match', 'ambiguous', 'not_found'].includes(v.setup_proposal.status) ? {
      status: v.setup_proposal.status, candidate_description: text(v.setup_proposal.candidate_description), quantity: decimal(v.setup_proposal.quantity), unit: text(v.setup_proposal.unit), source_stage: [202, 206, 604].includes(v.setup_proposal.source_stage) ? v.setup_proposal.source_stage : undefined, source_label: text(v.setup_proposal.source_label), evidence_state: text(v.setup_proposal.evidence_state), recipe_key: text(v.setup_proposal.recipe_key) || null,
    } : null,
    board_waste_fraction: '0.10',
    fee_assumption: fee && ['markup_known_cost', 'target_margin', 'award_reference_share'].includes(fee.basis) && decimal(fee.rate) !== null
      ? { basis: fee.basis, rate: fee.rate, rationale: text(fee.rationale) } : null,
    other_costs: rows(v.other_costs).map(x => ({
      name: text(x.name), amount: decimal(x.amount), basis: text(x.basis) || null,
      required: x.required !== false, explicit_zero_reason: text(x.explicit_zero_reason) || null,
      cost_treatment: ['direct_project', 'labor_support', 'general_and_administrative', 'site_supervision', 'fee_profit', 'unclassified'].includes(text(x.cost_treatment))
        ? text(x.cost_treatment) as 'direct_project' | 'labor_support' | 'general_and_administrative' | 'site_supervision' | 'fee_profit' | 'unclassified'
        : 'unclassified',
    })),
    ...(indirect_allowance_settings ? { indirect_allowance_settings } : {}),
    ...(labor_role_selections.length ? { labor_role_selections } : {}),
    site_staffing_roles,
    removed_site_staffing_role_kinds: removedSiteStaffingRoleKinds(v),
    ...(/^labor-plan-[a-f0-9]{32}$/.test(text(v.labor_plan_provenance)) ? { labor_plan_provenance: text(v.labor_plan_provenance) } : {}),
  };
}
function source(v: Record<string, any>): ApproximateSourceLine {
  let url: string | undefined;
  try { const parsed = new URL(v.source_url); if (parsed.protocol === 'https:' && !parsed.username && !parsed.password) url = parsed.href; } catch { /* No public attribution link. */ }
  const line_id = text(v.line_id).trim();
  return { label: text(v.label), amount: decimal(v.amount), source_label: text(v.source_label), source_date: text(v.source_date), geography_label: text(v.geography_label), assumptions: texts(v.assumptions), ...(url ? { source_url: url } : {}), ...( /^[a-z0-9][a-z0-9_-]{2,159}$/i.test(line_id) ? { line_id } : {}) };
}
const sourceAdjustments = (value: unknown): ApproximateSourceAdjustment[] | undefined => {
  if (value === undefined) return undefined;
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value.flatMap((item): ApproximateSourceAdjustment[] => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const raw = item as Record<string, unknown>;
    const line_id = text(raw.line_id).trim();
    const kind = raw.kind;
    const factor = decimal(raw.factor);
    const input_amount = decimal(raw.input_amount);
    const output_amount = decimal(raw.output_amount);
    const source_date = publicIsoDate(raw.source_date);
    const estimate_date = publicIsoDate(raw.estimate_date);
    const source_geography = publicExplanation(raw.source_geography);
    const estimate_geography = publicExplanation(raw.estimate_geography);
    const source_label = publicExplanation(raw.source_label);
    const explanation = publicExplanation(raw.explanation);
    const limitation = publicExplanation(raw.limitation) || undefined;
    const source_url = publicHttpsUrl(raw.source_url) || undefined;
    if (!/^[a-z0-9][a-z0-9_-]{2,159}$/i.test(line_id) || seen.has(line_id)
      || !['direct_local', 'wage_determination', 'pax_area_time', 'ppi_escalation', 'identity'].includes(String(kind))
      || factor === null || input_amount === null || output_amount === null || !source_date || !estimate_date
      || !source_geography || !estimate_geography || !source_label || !explanation) return [];
    seen.add(line_id);
    return [{ line_id, kind: kind as ApproximateSourceAdjustment['kind'], factor, input_amount, output_amount, source_date, estimate_date, source_geography, estimate_geography, source_label, ...(source_url ? { source_url } : {}), explanation, ...(limitation ? { limitation } : {}) }];
  }).slice(0, 100);
};
const publicHttpsUrl = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value.trim() || value.length > 2_048) return null;
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === 'https:' && !parsed.username && !parsed.password ? parsed.href : null;
  } catch {
    return null;
  }
};
const wageDeterminationUrl = (value: unknown): string | null => {
  const url = publicHttpsUrl(value);
  if (!url) return null;
  const parsed = new URL(url);
  return parsed.hostname === 'sam.gov' && /^\/wage-determination\/[A-Z]{2}\d{8}\/\d+$/.test(parsed.pathname)
    && !parsed.search && !parsed.hash ? url : null;
};
const serverOwnedProfit = (v: any, pricedSubtotal: string | null): Pick<ApproximateCostPlanPublic, 'profit_margin' | 'modeled_subtotal_range'> => {
  const profit = v?.profit_margin;
  const range = v?.modeled_subtotal_range;
  if (!profit || !range || pricedSubtotal === null || profit.low_rate !== '0.07' || profit.high_rate !== '0.12') return {};
  const base_amount = nonnegativeDecimal(profit.base_amount);
  const low = nonnegativeDecimal(profit.low);
  const high = nonnegativeDecimal(profit.high);
  const rangeLow = nonnegativeDecimal(range.low);
  const rangeHigh = nonnegativeDecimal(range.high);
  if (base_amount === null || Number(base_amount) <= 0 || low === null || high === null || rangeLow === null || rangeHigh === null
    || Number(low) > Number(high) || Number(rangeLow) > Number(rangeHigh)
    || Math.abs(Number(low) - Number(base_amount) * 0.07) > 0.011
    || Math.abs(Number(high) - Number(base_amount) * 0.12) > 0.011
    || Math.abs(Number(rangeLow) - Number(pricedSubtotal) - Number(low)) > 0.011
    || Math.abs(Number(rangeHigh) - Number(pricedSubtotal) - Number(high)) > 0.011) return {};
  return { profit_margin: { base_amount, low, high, low_rate: '0.07', high_rate: '0.12' }, modeled_subtotal_range: { low: rangeLow, high: rangeHigh } };
};
const publicIsoDate = (value: unknown): string | null => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
const workPackageId = (value: unknown): string | null => typeof value === 'string' && /^[a-z0-9][a-z0-9_-]{0,119}$/.test(value) ? value : null;
/**
 * Strict public allowlist for all-in installed work.  Extra raw source fields
 * are intentionally ignored rather than carried into the browser payload.
 */
const installedWorkLines = (value: unknown): ApproximateInstalledWorkLine[] => rows(value)
  .flatMap((raw): ApproximateInstalledWorkLine[] => {
    const work_package_id = workPackageId(raw.work_package_id);
    const label = publicExplanation(raw.label);
    const amount = decimal(raw.amount);
    const matched_quantity_label = publicExplanation(raw.matched_quantity_label);
    const evidence_label = publicExplanation(raw.evidence_label);
    const source_label = publicExplanation(raw.source_label);
    const source_date = publicIsoDate(raw.source_date);
    const geography_label = publicExplanation(raw.geography_label);
    const limitation = publicExplanation(raw.limitation);
    const source_url = publicHttpsUrl(raw.source_url);
    // An installed-work line is all-in.  Never accept a material-only or
    // otherwise unclassified source row as a replacement for recipe costs.
    if (raw.evidence_type !== 'installed_work' || !work_package_id || !label || amount === null
      || !matched_quantity_label || !evidence_label || !source_label || !source_date
      || !geography_label || !limitation || !source_url) return [];
    return [{
      work_package_id, label, amount, matched_quantity_label, evidence_label,
      source_label, source_date, geography_label, limitation, source_url,
    }];
  })
  .slice(0, 12);
const planningWorkLines = (value: unknown): ApproximatePlanningWorkLine[] => {
  if (!Array.isArray(value)) return [];
  const positiveDecimal = (value: unknown): string | null => {
    const parsed = nonnegativeDecimal(value);
    return parsed !== null && Number(parsed) > 0 ? parsed : null;
  };
  const seen = new Set<string>();
  // One-word public nouns such as "Nationwide" or "Installation" are valid;
  // lowercase machine keys and paths are not owner-facing explanations.
  const planningText = (value: unknown): string => {
    const sentence = publicExplanation(value);
    if (sentence) return sentence;
    const word = text(value).trim();
    return /^[A-Z][A-Za-z0-9()-]{1,80}$/.test(word) ? word : '';
  };
  return rows(value).flatMap((raw): ApproximatePlanningWorkLine[] => {
    const occurrence_id = text(raw.occurrence_id).trim();
    const label = planningText(raw.label);
    const amount = nonnegativeDecimal(raw.amount);
    const price_kind = raw.price_kind;
    const completeness = raw.completeness;
    const category = raw.category;
    const quantity_label = publicExplanation(raw.quantity_label);
    const unit_price_label = publicExplanation(raw.unit_price_label);
    const source_label = planningText(raw.source_label);
    const source_date = publicIsoDate(raw.source_date);
    const source_url = publicHttpsUrl(raw.source_url);
    const geography_label = planningText(raw.geography_label);
    const basis = publicExplanation(raw.basis);
    if (!/^(?:work|roof):[a-z0-9][a-z0-9_-]{2,119}$/i.test(occurrence_id) || seen.has(occurrence_id)
      || !label || amount === null
      || !['published_benchmark', 'adjusted_proxy', 'material_only_component', 'installed_work'].includes(price_kind)
      || !['complete', 'partial'].includes(completeness)
      || !['equipment', 'project_other', 'materials', 'installed_work'].includes(category)
      || typeof raw.included_in_subtotal !== 'boolean'
      || !quantity_label || !unit_price_label || !source_label || !source_date || !source_url
      || !geography_label || !basis) return [];
    const safeList = (items: unknown): string[] => texts(items).map(planningText).filter(Boolean).slice(0, 12);
    seen.add(occurrence_id);
    const display_group = raw.display_group === 'direct_materials' || raw.display_group === 'indirect_materials' ? raw.display_group : null;
    return [{ occurrence_id, label, amount, price_kind, completeness, category,
      ...(raw.roof_insulation_details && typeof raw.roof_insulation_details === 'object' ? { roof_insulation_details: {
        target_r: positiveDecimal(raw.roof_insulation_details.target_r) ?? '', achieved_r: positiveDecimal(raw.roof_insulation_details.achieved_r) ?? '',
        total_thickness_inches: positiveDecimal(raw.roof_insulation_details.total_thickness_inches) ?? '',
        evidence_basis: text(raw.roof_insulation_details.evidence_basis), configuration_id: text(raw.roof_insulation_details.configuration_id),
        specification_source_url: publicHttpsUrl(raw.roof_insulation_details.specification_source_url) ?? '',
        layers: rows(raw.roof_insulation_details.layers).slice(0, 8).flatMap(layer => {
          const quantity = positiveDecimal(layer.quantity); const unit_price = nonnegativeDecimal(layer.unit_price); const amount = nonnegativeDecimal(layer.amount);
          const coverage_sf = positiveDecimal(layer.coverage_sf); const r_value = positiveDecimal(layer.r_value); const thickness_inches = positiveDecimal(layer.thickness_inches);
          const source_url = publicHttpsUrl(layer.source_url); const label = planningText(layer.label); const source_date = publicIsoDate(layer.source_date);
          return quantity && unit_price !== null && amount !== null && coverage_sf && r_value && thickness_inches && label && source_url && source_date
            ? [{ label, quantity, unit_price, amount, coverage_sf, r_value, thickness_inches, source_url, source_date }] : [];
        }),
      } } : {}),
      ...(raw.display_group !== undefined ? { display_group } : {}),
      included_in_subtotal: raw.included_in_subtotal, quantity_label, unit_price_label,
      source_label, source_date, source_url, geography_label, basis,
      inclusions: safeList(raw.inclusions), exclusions: safeList(raw.exclusions), unknown_costs: safeList(raw.unknown_costs) }];
  }).slice(0, 60);
};
const unpricedExplicitComponents = (value: unknown): NonNullable<ApproximateCostPlanPublic['unpriced_explicit_components']> => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return rows(value).flatMap((raw) => {
    const occurrence_id = text(raw.occurrence_id).trim();
    const label = publicExplanation(raw.label) || (/^[A-Z][A-Za-z0-9() -]{1,80}$/.test(text(raw.label)) ? text(raw.label) : '');
    const reason = publicExplanation(raw.reason);
    const category = raw.category;
    if (!/^(?:component|work|roof):[a-z0-9][a-z0-9_-]{2,119}$/.test(occurrence_id)
      || seen.has(occurrence_id) || !label || !reason
      || (category !== 'materials' && category !== 'project_other')) return [];
    seen.add(occurrence_id);
    const display_group = raw.display_group === 'direct_materials' || raw.display_group === 'indirect_materials' ? raw.display_group : null;
    return [{ occurrence_id, label, category, ...(raw.display_group !== undefined ? { display_group } : {}), reason }];
  }).slice(0, 90);
};
const siteRoleLines = (value: unknown): NonNullable<ApproximateCostPlanPublic['site_role_lines']> => rows(value).flatMap((raw): NonNullable<ApproximateCostPlanPublic['site_role_lines']> => {
  const role_key = text(raw.role_key);
  const amount = decimal(raw.amount);
  const hours = decimal(raw.hours);
  const hourly_wage = decimal(raw.hourly_wage);
  const soc_code = text(raw.soc_code);
  const source_label = text(raw.source_label);
  if (!/^[a-z0-9][a-z0-9_-]{2,119}$/.test(role_key) || amount === null || hours === null || hourly_wage === null || !/^\d{2}-\d{4}$/.test(soc_code) || source_label !== 'BLS Occupational Employment and Wage Statistics (OEWS)') return [];
  const safe = source(raw);
  const evidence_kind = raw.evidence_kind === 'planning_default' ? 'planning_default' : raw.evidence_kind === 'verified_clause' ? 'verified_clause' : 'owner_cited_sow';
  return [{ role_key, label: text(raw.label), amount, hours, hourly_wage, hours_basis: text(raw.hours_basis), soc_code, duty_description: text(raw.duty_description), mapping_reason: text(raw.mapping_reason), evidence_kind, evidence_reference: text(raw.evidence_reference), evidence_quote: text(raw.evidence_quote), geography_label: text(raw.geography_label), geography_level: raw.geography_level === 'metro' || raw.geography_level === 'state' ? raw.geography_level : 'national', release_year: Number(raw.release_year), series_id: text(raw.series_id), source_label, ...(safe.source_url ? { source_url: safe.source_url } : {}), source_retrieved_at: text(raw.source_retrieved_at), fallback_reason: text(raw.fallback_reason) || null, limitation: text(raw.limitation) }];
}).slice(0, 12);
const unpricedSiteRoles = (value: unknown): NonNullable<ApproximateCostPlanPublic['unpriced_site_roles']> => {
  const seen = new Set<string>();
  return rows(value).flatMap((raw) => {
    const role_key = text(raw.role_key);
    const label = publicExplanation(raw.label);
    const reason = publicExplanation(raw.reason);
    if (!/^[a-z0-9][a-z0-9_-]{2,119}$/.test(role_key) || !label || !reason || seen.has(role_key)) return [];
    seen.add(role_key);
    return [{ role_key, label, reason }];
  }).slice(0, 12);
};
export function normalizeApproximateCostPlan(v: any): ApproximateCostPlanPublic | null {
  if (!v || typeof v !== 'object' || typeof v.scope_label !== 'string') return null;
  const materials_total = decimal(v.materials_total);
  const completeness = componentCompleteness(v.component_completeness);
  const result: ApproximateCostPlanPublic = {
    scope_label: text(v.scope_label), status_label: text(v.status_label), installed_quantity_label: text(v.installed_quantity_label), purchased_quantity_label: text(v.purchased_quantity_label), assumptions: texts(v.assumptions), exclusions: texts(v.exclusions),
    labor_total: decimal(v.labor_total), materials_total, priced_subtotal: decimal(v.priced_subtotal),
    ...serverOwnedProfit(v, decimal(v.priced_subtotal)),
    labor_lines: rows(v.labor_lines).map(x => ({ ...source(x), craft_label: text(x.craft_label), quantity_label: text(x.quantity_label), hours_per_unit: decimal(x.hours_per_unit) ?? '', hours: decimal(x.hours) ?? '', base_wage: decimal(x.base_wage) ?? '', fringe: decimal(x.fringe) ?? '', ...(wageDeterminationUrl(x.wage_determination_url) ? { wage_determination_url: wageDeterminationUrl(x.wage_determination_url) } : {}) })),
    ...(v.site_role_lines !== undefined ? { site_role_lines: siteRoleLines(v.site_role_lines) } : {}),
    ...(v.unpriced_site_roles !== undefined ? { unpriced_site_roles: unpricedSiteRoles(v.unpriced_site_roles) } : {}),
    ...(v.labor_plan_summary && typeof v.labor_plan_summary === 'object'
      && v.labor_plan_summary.coverage_state === 'covered_package_only'
      && ['applied', 'needs_review'].includes(v.labor_plan_summary.review_state)
      && ['low', 'medium', 'high'].includes(v.labor_plan_summary.confidence)
      && typeof v.labor_plan_summary.parallel_work === 'boolean'
      && publicExplanation(v.labor_plan_summary.provenance_label)
      && publicExplanation(v.labor_plan_summary.source_label)
      && Number(decimal(v.labor_plan_summary.average_concurrent_crew)) > 0
      && Number(decimal(v.labor_plan_summary.average_concurrent_crew)) <= 100
      && Number(decimal(v.labor_plan_summary.derived_site_days)) > 0
      && Number(decimal(v.labor_plan_summary.shift_hours)) > 0
      ? { labor_plan_summary: {
          provenance_label: publicExplanation(v.labor_plan_summary.provenance_label),
          source_label: publicExplanation(v.labor_plan_summary.source_label),
          coverage_state: 'covered_package_only' as const,
          review_state: v.labor_plan_summary.review_state as 'applied' | 'needs_review',
          confidence: v.labor_plan_summary.confidence as 'low' | 'medium' | 'high',
          average_concurrent_crew: decimal(v.labor_plan_summary.average_concurrent_crew)!,
          parallel_work: v.labor_plan_summary.parallel_work,
          derived_site_days: decimal(v.labor_plan_summary.derived_site_days)!,
          shift_hours: decimal(v.labor_plan_summary.shift_hours)!,
        } }
      : {}),
    ...(v.site_role_total !== undefined && decimal(v.site_role_total) !== null ? { site_role_total: decimal(v.site_role_total)! } : {}),
    material_lines: rows(v.material_lines).map(x => ({ ...source(x), specification: text(x.specification), sku: text(x.sku), consumption_label: text(x.consumption_label), purchased_quantity_label: text(x.purchased_quantity_label), package_label: text(x.package_label), unit_price: decimal(x.unit_price) ?? '' })),
    ...(v.indirect_allowances !== undefined ? { indirect_allowances: indirectAllowances(v.indirect_allowances) } : {}),
    ...(v.labor_role_plan !== undefined ? { labor_role_plan: laborRolePlan(v.labor_role_plan) } : {}),
    ...(v.source_adjustments !== undefined ? { source_adjustments: sourceAdjustments(v.source_adjustments) } : {}),
    ...(v.material_component_breakdown !== undefined ? { material_component_breakdown: materialComponentBreakdown(v.material_component_breakdown, materials_total) } : {}),
    ...(v.labor_coverage !== undefined ? { labor_coverage: laborCoverage(v.labor_coverage) } : {}),
    ...(v.sources_checked !== undefined ? { sources_checked: sourcesChecked(v.sources_checked) } : {}),
    ...(v.cost_evidence !== undefined ? { cost_evidence: localCostEvidence(v.cost_evidence) } : {}),
    ...(v.material_component_replacements !== undefined ? { material_component_replacements: materialComponentReplacements(v.material_component_replacements) } : {}),
    ...(completeness ? { component_completeness: completeness } : {}),
    ...(v.planning_work_lines !== undefined ? { planning_work_lines: planningWorkLines(v.planning_work_lines) } : {}),
    ...(v.unpriced_explicit_components !== undefined ? { unpriced_explicit_components: unpricedExplicitComponents(v.unpriced_explicit_components) } : {}),
    other_costs: rows(v.other_costs).map(x => {
      const estimate_category = x.estimate_category === 'labor' || x.estimate_category === 'materials' || x.estimate_category === 'project_other'
        ? x.estimate_category : undefined;
      return {
        label: text(x.label), amount: decimal(x.amount), basis: text(x.basis),
        ...(estimate_category ? { estimate_category } : {}),
        ...(x.required === false ? { required: false } : {}),
      };
    }),
    award_reference: publicAwardReference(v.award_reference),
    warnings: texts(v.warnings).filter(warning => warning !== OBSOLETE_MATERIAL_OBSERVATION_WARNING),
  };
  if (v.installed_work_lines !== undefined) {
    // Keep this renderer-only selection out of JSON serialization.  The
    // backend's raw `installed_work` classification is deliberately not a
    // browser/API payload value; only this already-allowlisted display line is
    // read by the ROM component.
    Object.defineProperty(result, 'installed_work_lines', {
      value: installedWorkLines(v.installed_work_lines), enumerable: false,
    });
  }
  return result;
}
